import jsPDF from 'jspdf';

const MARGIN = 72;
const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const LINE_HEIGHT = 16;

const orderedSections = [
  'Executive Summary',
  'Observations and Analysis',
  'Recommendations',
  'Conclusion'
];

const compressImage = async (url, maxDisplayWidthPt = 288) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const displayWidthPt = maxDisplayWidthPt;
      const dpi = 600;
      const displayWidthInches = displayWidthPt / 72;
      const renderWidthPx = dpi * displayWidthInches;
      const aspectRatio = img.naturalHeight / img.naturalWidth;
      const renderHeightPx = renderWidthPx * aspectRatio;

      const canvas = document.createElement('canvas');
      canvas.width = renderWidthPx;
      canvas.height = renderHeightPx;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, renderWidthPx, renderHeightPx);

      const base64 = canvas.toDataURL('image/jpeg', 1.0);

      resolve({ base64, width: displayWidthPt, height: displayWidthPt * aspectRatio });
    };
    img.onerror = reject;
    img.src = url;
  });
};

const addHeader = (doc, clientName, reportTitle) => {
  doc.setFontSize(10);
  doc.text(clientName || '', PAGE_WIDTH - MARGIN, MARGIN, { align: 'right' });
  doc.setFontSize(9);
  doc.text(reportTitle || '', PAGE_WIDTH - MARGIN, MARGIN + 14, { align: 'right' });
};

const addFooter = async (doc, logoUrl, footerHeightPt) => {
  if (!logoUrl) return;
  try {
    const { base64, width, height } = await compressImage(logoUrl, 288);
    const x = (PAGE_WIDTH - width) / 2;
    const y = PAGE_HEIGHT - MARGIN + 20 - height;
    doc.addImage(base64, 'JPEG', x, y, width, height);
  } catch (err) {
    console.warn('Footer logo skipped:', err);
  }
};

const renderMarkdownParagraph = async (doc, text, font, x, y, maxWidth, footerHeightPt) => {
  const imageMatch = text.match(/\[\[image:(.+?)\|(.+?)\]\]/);
  if (imageMatch) {
    const url = imageMatch[1];
    const caption = imageMatch[2];

    try {
      const { base64, width, height } = await compressImage(url, 288);
      const centerX = (PAGE_WIDTH - width) / 2;
      if (y + height > PAGE_HEIGHT - MARGIN - footerHeightPt - 40) {
        doc.addPage();
        y = MARGIN + 40;
        addHeader(doc);
      }

      doc.addImage(base64, 'JPEG', centerX, y, width, height);
      y += height + 12;

      doc.setFont(font, 'italic');
      doc.setFontSize(10);
      doc.text(caption, PAGE_WIDTH / 2, y, { align: 'center' });
      y += LINE_HEIGHT;

      doc.setFont(font, 'normal');
    } catch (err) {
      console.warn('Failed to insert image:', url, err);
    }

    return y;
  }

  let style = 'normal';
  let size = 11;
  if (text.startsWith('### ')) {
    style = 'bold';
    size = 12;
    text = text.slice(4);
  } else if (text.startsWith('## ')) {
    style = 'bold';
    size = 14;
    text = text.slice(3);
  } else if (text.startsWith('# ')) {
    style = 'bold';
    size = 16;
    text = text.slice(2);
  }

  doc.setFont(font, style);
  doc.setFontSize(size);

  if (text.startsWith('- ')) {
    const items = text.split(/^- /gm).filter(i => i.trim());
    for (const item of items) {
      const bullet = '\u2022 ' + item.trim();
      const lines = doc.splitTextToSize(bullet, maxWidth);
      for (const line of lines) {
        if (y > PAGE_HEIGHT - MARGIN - footerHeightPt - 20) {
          doc.addPage();
          y = MARGIN + 40;
        }
        doc.text(line, x, y);
        y += LINE_HEIGHT;
      }
    }
    return y;
  }

  if (/^\d+\. /.test(text)) {
    const items = text.split(/(?=\n\d+\. )/gm);
    for (const item of items) {
      const lines = doc.splitTextToSize(item.trim(), maxWidth);
      for (const line of lines) {
        if (y > PAGE_HEIGHT - MARGIN - footerHeightPt - 20) {
          doc.addPage();
          y = MARGIN + 40;
        }
        doc.text(line, x, y);
        y += LINE_HEIGHT;
      }
    }
    return y;
  }

  const lines = doc.splitTextToSize(text, maxWidth);
  for (const line of lines) {
    if (y > PAGE_HEIGHT - MARGIN - footerHeightPt - 20) {
      doc.addPage();
      y = MARGIN + 40;
    }
    doc.text(line, x, y);
    y += LINE_HEIGHT;
  }

  return y;
};

const addSection = async (doc, title, content, font, clientName, reportTitle, logoUrl, footerHeightPt) => {
  doc.addPage();
  addHeader(doc, clientName, reportTitle);
  let y = MARGIN + 40;
  doc.setFont(font, 'bold');
  doc.setFontSize(14);
  doc.text(title.toUpperCase(), MARGIN, y);
  y += 20;

  const paragraphs = typeof content === 'string'
    ? content.split('\n').filter(p => typeof p === 'string' && p.trim())
    : [];

  for (const p of paragraphs) {
    y = await renderMarkdownParagraph(doc, p, font, MARGIN, y, PAGE_WIDTH - 2 * MARGIN, footerHeightPt);
  }

  await addFooter(doc, logoUrl, footerHeightPt);
};

const generateFullReportPdf = async ({
  projectName,
  coverData,
  sectionData,
  clientName,
  font,
  logoUrl,
  footerHeightPt,
  customSections = []
}) => {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const reportTitle = coverData.projectDescription || '';
  doc.setProperties({
    title: `${projectName || 'Report'}`,
    subject: reportTitle,
    author: clientName || '',
    creator: 'Vilano Consulting Report Generator'
  });
  doc.setFont(font || 'helvetica', 'normal');

  // Cover Page
  doc.setFontSize(22);
  doc.setFont(font, 'bold');
  doc.text(projectName || 'Project Name', PAGE_WIDTH / 2, MARGIN + 40, { align: 'center' });
  doc.setFontSize(16);
  doc.text(reportTitle, PAGE_WIDTH / 2, MARGIN + 65, { align: 'center' });

  if (coverData.coverImageUrl) {
    try {
      const { base64, width, height } = await compressImage(coverData.coverImageUrl, 288);
      const x = (PAGE_WIDTH - width) / 2;
      const y = MARGIN + 90;
      doc.addImage(base64, 'JPEG', x, y, width, height);
    } catch (err) {
      console.warn('Cover image error:', err);
    }
  }

  doc.setFont(font, 'bold');
  doc.setFontSize(14);
  doc.text(clientName || '', PAGE_WIDTH / 2, PAGE_HEIGHT - 140, { align: 'center' });

  doc.setFont(font, 'normal');
  doc.setFontSize(12);
  if (coverData.clientAddress) {
    coverData.clientAddress.split('\n').forEach((line, i) => {
      doc.text(line, PAGE_WIDTH / 2, PAGE_HEIGHT - 120 + i * 14, { align: 'center' });
    });
  }

  await addFooter(doc, logoUrl, footerHeightPt);

  // Table of Contents
  doc.addPage();
  addHeader(doc, clientName, reportTitle);
  let y = MARGIN + 40;
  doc.setFont(font, 'bold');
  doc.setFontSize(14);
  doc.text('TABLE OF CONTENTS', MARGIN, y);
  y += 20;
  doc.setFont(font, 'normal');
  doc.setFontSize(11);

  const allSections = [...orderedSections, ...customSections];

  const toc = allSections
    .filter(s => typeof sectionData[s] === 'string' && sectionData[s].trim())
    .map((s, i) => [s.toUpperCase(), `${i + 2}`]); // Page numbers start after cover + TOC

  toc.forEach(([title, page], i) => {
    const rowY = y + i * 20;
    doc.text(title, MARGIN, rowY);
    doc.text(page, PAGE_WIDTH - MARGIN, rowY, { align: 'right' });
  });

  await addFooter(doc, logoUrl, footerHeightPt);

  // Sections
  for (const section of allSections) {
    if (typeof sectionData[section] === 'string' && sectionData[section].trim()) {
      await addSection(doc, section, sectionData[section], font, clientName, reportTitle, logoUrl, footerHeightPt);
    }
  }

  doc.save(`${projectName || 'Report'}.pdf`);
};

export default generateFullReportPdf;
